import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import AudioPlayer from './AudioPlayer';

const TipContentRenderer = ({ 
  content, 
  contentHtml = null,
  musicxmlId = null,
  className = '',
  style = {}
}) => {
  const { user } = useAuth();
  const [audioClips, setAudioClips] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load audio clips when component mounts or content changes
  useEffect(() => {
    loadAudioClips();
  }, [content, contentHtml, musicxmlId]);

  const loadAudioClips = async () => {
    try {
      // Extract audio clip names from the content
      const contentToScan = contentHtml || content || '';
      const audioRegex = /\[audio:([^\]]+)\]/g;
      const clipNames = [];
      let match;
      
      while ((match = audioRegex.exec(contentToScan)) !== null) {
        const clipName = match[1];
        if (!clipNames.includes(clipName)) {
          clipNames.push(clipName);
        }
      }
      
      if (clipNames.length === 0) {
        setLoading(false);
        return;
      }
      
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const params = new URLSearchParams({
        operation: 'getAudioClipsByNames',
        names: clipNames.join(',')
      });
      
      const response = await fetch(`${API_BASE}/api/audio-clips?${params}`);
      
      if (response.ok) {
        const clipsData = await response.json();
        setAudioClips(clipsData);
      }
    } catch (err) {
      console.error('Failed to load audio clips for rendering:', err);
    } finally {
      setLoading(false);
    }
  };

  const parseContentWithAudio = (content) => {
    if (!content) return [];
    
    const parts = [];
    const audioRegex = /\[audio:([^\]]+)\]/g;
    let lastIndex = 0;
    let match;
    
    while ((match = audioRegex.exec(content)) !== null) {
      // Add text before the audio markup
      if (match.index > lastIndex) {
        const textContent = content.substring(lastIndex, match.index);
        if (textContent.trim()) {
          parts.push({
            type: 'text',
            content: textContent
          });
        }
      }
      
      // Add audio component
      const clipName = match[1];
      const clip = audioClips.find(c => c.name === clipName);
      parts.push({
        type: 'audio',
        clipName: clipName,
        clip: clip
      });
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < content.length) {
      const remainingText = content.substring(lastIndex);
      if (remainingText.trim()) {
        parts.push({
          type: 'text',
          content: remainingText
        });
      }
    }
    
    return parts;
  };

  const parseHtmlContentWithAudio = (htmlContent) => {
    if (!htmlContent) return null;
    
    const audioRegex = /\[audio:([^\]]+)\]/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    let key = 0;
    
    while ((match = audioRegex.exec(htmlContent)) !== null) {
      // Add content before the audio markup
      if (match.index > lastIndex) {
        const beforeText = htmlContent.substring(lastIndex, match.index);
        parts.push(
          <span 
            key={`text-${key++}`} 
            dangerouslySetInnerHTML={{ __html: beforeText }}
          />
        );
      }
      
      // Add audio player
      const clipName = match[1];
      const clip = audioClips.find(c => c.name === clipName);
      parts.push(
        <div key={`audio-${key++}`} style={{ 
          display: 'inline-block',
          margin: '4px 8px',
          verticalAlign: 'middle'
        }}>
          {clip ? (
            <AudioPlayer
              clipName={clip.name}
              fileUrl={clip.file_url}
              compact={true}
              showTitle={true}
            />
          ) : (
            <span 
              style={{
                background: '#fee2e2',
                color: '#dc2626',
                padding: '2px 6px',
                borderRadius: '4px',
                fontSize: '12px',
                display: 'inline-block'
              }}
              title={`Audio clip "${clipName}" not found`}
            >
              🎵 {clipName} (not available)
            </span>
          )}
        </div>
      );
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining content
    if (lastIndex < htmlContent.length) {
      const remainingText = htmlContent.substring(lastIndex);
      parts.push(
        <span 
          key={`text-${key++}`} 
          dangerouslySetInnerHTML={{ __html: remainingText }}
        />
      );
    }
    
    // If no audio markup found, just render the HTML normally
    if (parts.length === 0) {
      return <div dangerouslySetInnerHTML={{ __html: htmlContent }} />;
    }
    
    return <div>{parts}</div>;
  };

  // If we have HTML content, render it as HTML with audio support
  if (contentHtml && contentHtml !== content) {
    if (loading) {
      return (
        <div className={className} style={{ ...style }}>
          <div dangerouslySetInnerHTML={{ 
            __html: contentHtml.replace(/\[audio:([^\]]+)\]/g, '🎵 $1 (loading...)') 
          }} />
        </div>
      );
    }

    const processedContent = parseHtmlContentWithAudio(contentHtml);
    return (
      <div className={className} style={{ ...style }}>
        {processedContent}
      </div>
    );
  }

  // Fallback to plain text rendering for legacy content
  // If no audio markup found, just render as plain text
  if (!content || !content.includes('[audio:')) {
    return (
      <div className={className} style={{ ...style, whiteSpace: 'pre-wrap' }}>
        {content}
      </div>
    );
  }

  if (loading) {
    return (
      <div className={className} style={{ ...style, whiteSpace: 'pre-wrap' }}>
        {content.replace(/\[audio:([^\]]+)\]/g, '🎵 $1 (loading...)')}
      </div>
    );
  }

  const contentParts = parseContentWithAudio(content);

  if (contentParts.length === 0) {
    return (
      <div className={className} style={{ ...style, whiteSpace: 'pre-wrap' }}>
        {content}
      </div>
    );
  }

  return (
    <div className={className} style={{ ...style }}>
      {contentParts.map((part, index) => {
        if (part.type === 'text') {
          return (
            <span key={index} style={{ whiteSpace: 'pre-wrap' }}>
              {part.content}
            </span>
          );
        } else if (part.type === 'audio') {
          if (part.clip) {
            return (
              <div key={index} style={{ 
                display: 'inline-block',
                margin: '4px 8px',
                verticalAlign: 'middle'
              }}>
                <AudioPlayer
                  clipName={part.clip.name}
                  fileUrl={part.clip.file_url}
                  compact={true}
                  showTitle={true}
                />
              </div>
            );
          } else {
            // Show fallback for missing clips
            return (
              <span 
                key={index} 
                style={{
                  background: '#fee2e2',
                  color: '#dc2626',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  margin: '0 4px',
                  display: 'inline-block'
                }}
                title={`Audio clip "${part.clipName}" not found`}
              >
                🎵 {part.clipName} (not available)
              </span>
            );
          }
        }
        return null;
      })}
    </div>
  );
};

export default TipContentRenderer;